import React, { memo, useState, useEffect } from 'react';
import { Button, Flex, Text, Collapse, FlexColumn, Callout, Tooltip, Popover } from 'core/components';
import { TextArea } from 'core/form';
import CopyToClipboard from 'app/components/CopyToClipboardButton';

import { FiCopy, FiThumbsUp, FiThumbsDown } from 'react-icons/fi';
import Markdown from 'app/components/markdown/Markdown';
import { ReasoningDetails, ReasoningContent, StepContainer } from '../KaizenChatSession.styles';
import KaizenToolResult from '../KaizenToolResult';
import FeedbackModel from '../models/FeedbackModel';
import { stepHasDeviceToolCall } from '../util/toolErrorUtils';
import KaizenErrorMessage from './KaizenErrorMessage';

// replaces /configs markdown links with plain text.
function stripConfigLinks(text) {
  return text
    ? text.replace(/\[([^\]]+)\]\((?:https?:\/\/[^/]*)?(?:\/v4\/infrastructure\/devices\/[^)]+\/configs)\)/g, '$1')
    : text;
}

// Memoized step component to prevent unnecessary re-renders
const StepComponent = memo(
  ({
    step,
    stepIndex,
    turn,
    isAsker,
    askerName,
    $auth,
    isStepReasoningOpen,
    onToggleReasoning,
    onSelectToolResult,
    onSendToExplorer,
    isCopilotMode,
    inlineToolResults,
    isExistingChatLoading,
    sessionModel
  }) => {
    const stepId = step.id || stepIndex;
    const { llm_response = {}, toolCalls } = step;

    const hasFinalAnswer =
      llm_response?.final_answer && step.processing_status === 'COMPLETED' && step.parent_step_id === null;
    const hasSubAgentCalls = toolCalls?.some((tc) => tc.agent_name && tc.agent_name !== 'kaizen');

    const [showThankYouMessage, setShowThankYouMessage] = useState(false);
    const [showFeedbackPopover, setShowFeedbackPopover] = useState(false);

    // Memoize feedbackModel so it's only created when dependencies change
    const feedbackModel = React.useMemo(() => {
      if (hasFinalAnswer) {
        const currentUserFeedbackRecord = turn.feedback?.find(
          (item) => String(item.user_id) === String($auth.activeUser.id)
        );
        if (currentUserFeedbackRecord) {
          return new FeedbackModel(currentUserFeedbackRecord);
        }
        return new FeedbackModel();
      }
      return null;
    }, [hasFinalAnswer, turn.feedback, $auth.activeUser.id]);

    const [feedbackText, setFeedbackText] = useState(feedbackModel?.get('user_comment'));

    const handleThumbsUpClick = () => {
      if (feedbackModel.get('user_rating') === 1) {
        return;
      }
      setShowThankYouMessage(true);
      feedbackModel.save(
        { user_rating: 1, agent_turn_id: turn.id },
        { sendProvidedValuesOnly: true, toast: false, optimistic: true }
      );
    };

    const handleThumbsDownClick = () => {
      if (feedbackModel.get('user_rating') === -1) {
        setShowFeedbackPopover(true);
        return;
      }
      setShowThankYouMessage(true);
      setShowFeedbackPopover(true);
      feedbackModel.save(
        { user_rating: -1, agent_turn_id: turn.id },
        { sendProvidedValuesOnly: true, toast: false, optimistic: true }
      );
    };

    const handleFeedbackSubmit = () => {
      // Submit the feedback with optional text
      setShowFeedbackPopover(false);
      setShowThankYouMessage(true);
      feedbackModel.save(
        { user_comment: String(feedbackText).trim().substring(0, 5000), agent_turn_id: turn.id },
        { sendProvidedValuesOnly: false, toast: false, optimistic: true }
      );
    };

    const handlePopoverClose = () => {
      setShowFeedbackPopover(false);
      // setFeedbackText('');
    };

    // Hide thank you message after 3 seconds
    useEffect(() => {
      if (showThankYouMessage) {
        const timer = setTimeout(() => {
          setShowThankYouMessage(false);
        }, 3000);

        return () => clearTimeout(timer);
      }
      return undefined;
    }, [showThankYouMessage]);

    const isFinalAnswer =
      llm_response?.final_answer && step.processing_status === 'COMPLETED' && step.parent_step_id === null;

    // True when the config tool ran but was denied for this device (e.g. wrong label).
    const stepHasConfigToolDenialInTurn = toolCalls?.some(
      (call) =>
        call.tool_name === 'device_config' &&
        call.status === 'FAILURE' &&
        call.error &&
        (call.error.includes('Configuration history and related links are not available') ||
          call.error.includes('You do not have permission for:'))
    );

    // True if the user has both the company feature flag and the config RBAC permission.
    // Mirrors the same gate that controls whether the Configs tab is visible.
    const userCanViewConfigs =
      $auth.hasPermission('recon.deviceConfigFetch.enabled', { overrideForSudo: false }) &&
      $auth.hasRbacPermissions(['device.config::read']);

    // Strip /configs links when the user has no config access globally, or when
    // the config tool was denied for this specific device in the current turn.
    const shouldStripConfigLinks = !userCanViewConfigs || stepHasConfigToolDenialInTurn;

    return (
      <StepContainer>
        {llm_response?.reasoning && !hasSubAgentCalls && (
          <Flex alignItems="center" gap={1}>
            <Button
              small
              minimal
              color="muted"
              icon={isStepReasoningOpen ? 'caret-up' : 'caret-down'}
              style={{ alignSelf: 'flex-start' }}
              text={isStepReasoningOpen ? 'Hide reasoning' : 'Show reasoning'}
              onClick={() => onToggleReasoning(stepId)}
            />
          </Flex>
        )}
        <Flex flexDirection="column" gap={1}>
          {llm_response?.reasoning && !hasSubAgentCalls && (
            <Collapse isOpen={isStepReasoningOpen} title="Reasoning" defaultOpen>
              <ReasoningDetails>
                <ReasoningContent>
                  <Markdown>{llm_response.reasoning}</Markdown>
                </ReasoningContent>
              </ReasoningDetails>
            </Collapse>
          )}

          {toolCalls?.length > 0 && (
            <FlexColumn flexDirection="column" gap="8px">
              {/* Sub-agent calls belong to the sub-agent panel, not the main thread */}
              {toolCalls
                .filter((toolCall) => !toolCall.agent_name || toolCall.agent_name === 'kaizen')
                .map((toolCall) => (
                  <KaizenToolResult
                    key={toolCall.id || toolCall.tool_tracking_id}
                    tool={toolCall}
                    step={step}
                    turn={turn}
                    isAsker={isAsker}
                    askerName={askerName}
                    onSelectToolResult={onSelectToolResult}
                    onSendToExplorer={onSendToExplorer}
                    isCopilotMode={isCopilotMode}
                    inline={inlineToolResults}
                    isExistingChatLoading={isExistingChatLoading}
                    sessionModel={sessionModel}
                  />
                ))}
            </FlexColumn>
          )}

          {isFinalAnswer && (
            <Flex flexDirection="column" gap={1} data-final-answer>
              <Markdown>
                {shouldStripConfigLinks ? stripConfigLinks(llm_response.final_answer) : llm_response.final_answer}
              </Markdown>
              <Flex alignItems="center" gap={1}>
                <Tooltip content="Copy response" position="bottom">
                  <CopyToClipboard
                    intent="default"
                    text={
                      shouldStripConfigLinks ? stripConfigLinks(llm_response.final_answer) : llm_response.final_answer
                    }
                  >
                    <Button minimal icon={FiCopy} />
                  </CopyToClipboard>
                </Tooltip>

                <Button
                  minimal
                  icon={FiThumbsUp}
                  intent={feedbackModel.get('user_rating') === 1 ? 'success' : 'default'}
                  onClick={() => handleThumbsUpClick()}
                />

                <Popover
                  isOpen={showFeedbackPopover}
                  onClose={handlePopoverClose}
                  position="top"
                  minimal={false}
                  content={
                    <Flex flexDirection="column" gap={1} p={2} style={{ width: '330px', height: 'auto' }}>
                      <Text fontWeight="bold">Help us improve AI Advisor!</Text>
                      <Text color="muted" fontSize={13}>
                        Tell us what went wrong
                      </Text>
                      <TextArea
                        placeholder="Your feedback..."
                        value={feedbackText}
                        rows={2}
                        onChange={(e) => setFeedbackText(e.target.value)}
                        style={{ resize: 'none' }}
                        autoFocus
                      />
                      <Flex justifyContent="flex-end" gap={1}>
                        <Button minimal text="Cancel" onClick={handlePopoverClose} />
                        <Button intent="primary" text="Submit" onClick={handleFeedbackSubmit} />
                      </Flex>
                    </Flex>
                  }
                >
                  <Button
                    minimal
                    icon={FiThumbsDown}
                    intent={feedbackModel.get('user_rating') === -1 ? 'danger' : 'default'}
                    onClick={() => handleThumbsDownClick('down')}
                  />
                </Popover>

                {showThankYouMessage && (
                  <Text fontSize={13} color="muted">
                    Thank you! Your feedback helps make AI Advisor better for everyone.
                  </Text>
                )}
              </Flex>
            </Flex>
          )}

          {step.error_message && step.error_message !== 'SCHEMA_FAILED' && (
            <Callout intent="danger">
              <Text small>
                Error:{' '}
                <KaizenErrorMessage
                  message={step.error_message}
                  showMessage={stepHasDeviceToolCall(toolCalls)}
                  fallback="something went wrong"
                />
              </Text>
            </Callout>
          )}
        </Flex>
      </StepContainer>
    );
  }
);

StepComponent.displayName = 'StepComponent';

export default StepComponent;
